import { Avatar, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import FollowMeIcons from '../components/FollowMeIcons/FollowMeIcons';
import { config } from '../config/config';
import { convertMarkdownFileToPdfStream } from '../services/markdownToPdfService';

interface ResumeDownloadState {
    fileName: string;
    url: string;
}

const AbouMe = () => {

    const [aboutText, setAboutText] = useState("");
    const [resumeDownload, setResumeDownload] = useState<ResumeDownloadState | null>(null);
    const [resumeLoadError, setResumeLoadError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        let pdfObjectUrl: string | null = null;

        const loadPageContent = async () => {
            const [aboutResponse, resumeResponse] = await Promise.all([
                fetch(config.ABOUT_ME_PAGE.ABOUT_ME_CONTENT_PATH),
                fetch(config.ABOUT_ME_PAGE.RESUME_CONTENT_PATH),
            ]);

            if (!aboutResponse.ok) {
                throw new Error(`Failed to load about me content from ${config.ABOUT_ME_PAGE.ABOUT_ME_CONTENT_PATH}.`);
            }

            if (!resumeResponse.ok) {
                throw new Error(`Failed to load resume content from ${config.ABOUT_ME_PAGE.RESUME_CONTENT_PATH}.`);
            }

            const [aboutContent, resumeContent] = await Promise.all([
                aboutResponse.text(),
                resumeResponse.blob(),
            ]);
            const resumeFileName = config.ABOUT_ME_PAGE.RESUME_CONTENT_PATH.split('/').pop() ?? 'resume.md';
            const resumeFile = new File(
                [resumeContent],
                resumeFileName,
                { type: resumeContent.type || 'text/markdown' }
            );
            const pdfDocument = await convertMarkdownFileToPdfStream(resumeFile);
            const pdfBlob = await new Response(pdfDocument.stream, {
                headers: {
                    'Content-Type': pdfDocument.mimeType,
                },
            }).blob();

            pdfObjectUrl = URL.createObjectURL(pdfBlob);

            if (!isMounted) {
                URL.revokeObjectURL(pdfObjectUrl);
                return;
            }

            setAboutText(aboutContent);
            setResumeDownload({
                fileName: pdfDocument.fileName,
                url: pdfObjectUrl,
            });
            setResumeLoadError(null);
        };

        loadPageContent().catch((error) => {
            if (!isMounted) {
                return;
            }

            setResumeDownload(null);
            setResumeLoadError(error instanceof Error ? error.message : 'Failed to prepare the resume download.');
        });

        return () => {
            isMounted = false;

            if (pdfObjectUrl) {
                URL.revokeObjectURL(pdfObjectUrl);
            }
        };
    }, []);

        return (
                <Grid container spacing={2} sx={{ pt: { xs: 3, md: 4 }, px: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
                        <Grid item xs={12} md={4} lg={3} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, alignItems: 'flex-start', pt: { xs: 2, md: 4 } }}>
                            <Avatar
                                alt={config.ABOUT_ME_PAGE.AVATAR_ALT_TAG}
                                src={config.ABOUT_ME_PAGE.AVATAR_SRC}
                                sx={{ width: { xs: 160, sm: 220, md: 280 }, height: { xs: 160, sm: 220, md: 280 } }}
                            />
                        </Grid>
                        <Grid item xs={12} md={8} lg={9} sx={{ pt: { xs: 1, md: 4 } }}>
                            <ReactMarkdown remarkPlugins={[gfm]}>{aboutText}</ReactMarkdown>
                            <p>
                                Download my resume:{' '}
                                {resumeDownload ? (
                                        <a href={resumeDownload.url} download={resumeDownload.fileName}>Resume</a>
                                ) : (
                                        resumeLoadError ?? 'Preparing resume...'
                                )}
                                {/* Download my resume: <a href={config.ABOUT_ME_PAGE.RESUME_CONTENT_PDF_PATH} download>Resume</a> */}
                            </p>
                            <h2>Follow Me</h2>
                            <FollowMeIcons />
                        </Grid>
                </Grid>
        )
};

export default AbouMe;
